import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Wifi,
  WifiOff,
  Activity,
  Target,
  TrendingUp,
  Clock,
  BarChart3,
  AlertCircle
} from 'lucide-react';
import { useLiveShots } from '@/hooks/useLiveShots';
import { ShotData } from '../types';

interface LiveMonitorProps {
  sessionId?: string;
  className?: string;
}

export const LiveMonitor: React.FC<LiveMonitorProps> = ({ sessionId, className = '' }) => {
  const {
    liveShots,
    lastShot,
    isLive,
    isConnected,
    simulateShot,
    clearShots,
    shotCount
  } = useLiveShots({
    sessionId,
    autoJoin: true,
    maxShots: 20,
  });

  const [sessionStats, setSessionStats] = useState({
    avgDistance: 0,
    avgQuality: 0,
    bestShot: null as ShotData | null,
    shotsPerMinute: 0,
    sessionDuration: 0,
  });

  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');

  useEffect(() => {
    if (isConnected) {
      setConnectionStatus('connected');
    } else if (isLive) {
      setConnectionStatus('connecting');
    } else {
      setConnectionStatus('disconnected');
    }
  }, [isConnected, isLive]);

  // Calculate session statistics
  useEffect(() => {
    if (liveShots.length === 0) return;

    const totalDistance = liveShots.reduce((sum, shot) => sum + shot.distance, 0);
    const avgDistance = totalDistance / liveShots.length;

    const qualityScore = (quality: string) => {
      switch (quality) {
        case 'excellent': return 4;
        case 'good': return 3;
        case 'fair': return 2;
        case 'poor': return 1;
        default: return 0;
      }
    };

    const totalQuality = liveShots.reduce((sum, shot) => sum + qualityScore(shot.quality), 0);
    const avgQuality = totalQuality / liveShots.length;

    const bestShot = liveShots.reduce((best, shot) => 
      (!best || shot.distance > best.distance) ? shot : best, 
      null as ShotData | null
    );

    // Calculate shots per minute (simplified)
    const sessionDuration = 10; // Mock duration in minutes
    const shotsPerMinute = liveShots.length / sessionDuration;

    setSessionStats({
      avgDistance,
      avgQuality,
      bestShot,
      shotsPerMinute,
      sessionDuration,
    });
  }, [liveShots]);

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-yellow-100 text-yellow-800';
      case 'fair': return 'bg-orange-100 text-orange-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="h-5 w-5 text-green-600" />;
      case 'connecting':
        return <Wifi className="h-5 w-5 text-yellow-600 animate-pulse" />;
      case 'disconnected':
        return <WifiOff className="h-5 w-5 text-red-600" />;
    }
  };

  const getConnectionBadge = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Badge className="bg-green-100 text-green-800">Connected</Badge>;
      case 'connecting':
        return <Badge className="bg-yellow-100 text-yellow-800">Connecting...</Badge>;
      case 'disconnected':
        return <Badge className="bg-red-100 text-red-800">Disconnected</Badge>;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Connection Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getConnectionIcon()}
              <div>
                <CardTitle>Live Monitoring</CardTitle>
                <CardDescription>Real-time shot tracking via WebSocket</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getConnectionBadge()}
              {isLive && (
                <Badge variant="outline" className="animate-pulse">
                  <Activity className="h-3 w-3 mr-1" />
                  Live
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Session Statistics */}
      {shotCount > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Shots</p>
                  <p className="text-2xl font-bold">{shotCount}</p>
                </div>
                <Target className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Distance</p>
                  <p className="text-2xl font-bold">{sessionStats.avgDistance.toFixed(1)}m</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Best Shot</p>
                  <p className="text-2xl font-bold">
                    {sessionStats.bestShot?.distance.toFixed(1)}m
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Shot Rate</p>
                  <p className="text-2xl font-bold">{sessionStats.shotsPerMinute.toFixed(1)}/min</p>
                </div>
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Latest Shot Alert */}
      {lastShot && (
        <Alert className="border-blue-200 bg-blue-50">
          <Activity className="h-4 w-4" />
          <AlertTitle>Latest Shot</AlertTitle>
          <AlertDescription>
            <div className="flex items-center gap-4 mt-2">
              <Badge className="capitalize">{lastShot.shotType}</Badge>
              <Badge className={getQualityColor(lastShot.quality)}>{lastShot.quality}</Badge>
              <span className="font-semibold">{lastShot.distance.toFixed(1)}m</span>
              <span className="text-sm text-gray-600">
                Exit Velocity: {lastShot.exitVelocity.toFixed(1)} m/s
              </span>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Shot Feed */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Live Shot Feed</CardTitle>
            <div className="flex items-center gap-2">
              {import.meta.env.DEV && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={simulateShot}
                  className="flex items-center gap-2"
                >
                  <Target className="h-4 w-4" />
                  Simulate
                </Button>
              )}
              {shotCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearShots}
                  className="flex items-center gap-2"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {shotCount === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Waiting for shots...</p>
              <p className="text-sm mt-2">Shots will appear here in real-time as they are recorded</p>
            </div>
          ) : (
            <ScrollArea className="h-96">
              <div className="space-y-3">
                {liveShots.map((shot, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      index === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">#{shotCount - index}</span>
                          <Badge className="capitalize">{shot.shotType}</Badge>
                          <Badge className={getQualityColor(shot.quality)}>
                            {shot.quality}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Distance:</span>
                            <span className="ml-1 font-medium">{shot.distance.toFixed(1)}m</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Exit Velocity:</span>
                            <span className="ml-1 font-medium">{shot.exitVelocity.toFixed(1)} m/s</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Launch Angle:</span>
                            <span className="ml-1 font-medium">{shot.launchAngle.toFixed(1)}°</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Power Transfer:</span>
                            <span className="ml-1 font-medium">{(shot.powerTransfer * 100).toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                      {index === 0 && (
                        <Badge variant="secondary" className="animate-pulse">
                          NEW
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Connection Help */}
      {!isConnected && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Connection Required</AlertTitle>
          <AlertDescription>
            Live monitoring requires an active WebSocket connection. Make sure:
            <ul className="mt-2 list-disc list-inside">
              <li>The API server is running on port 3000</li>
              <li>The Socket.IO server is running on port 3001</li>
              <li>You are authenticated with a valid token</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default LiveMonitor;